import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Calendar, 
  Download, 
  Filter,
  Eye,
  FileText,
  PieChart,
  Activity,
  MousePointer2
} from 'lucide-react';
import { statisticsApi } from '@/api/raynetClient';
import { SubmissionsTable } from './SubmissionsTable';
import { PDFExport } from './PDFExport';
import { StatisticsDrillDownModal, DrillDownData } from './StatisticsDrillDownModal';

interface StatisticsOverview {
  overview: {
    totalSubmissions: number;
    avgResponseTime: number;
    conversionRate: {
      completed: number;
      total: number;
    };
  };
  statusDistribution: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  monthlyTrends: Array<{
    month: string;
    count: number;
  }>;
  representativePerformance: Array<{
    representative: string;
    count: number;
  }>;
  marketInsights: {
    designImpressions: Array<{
      impression: string;
      count: number;
    }>;
    cooperationInterest: Array<{
      interest: string;
      count: number;
    }>;
    brandReactions: Array<{
      reaction: string;
      count: number;
    }>;
    topCompetitors: Array<{
      competitor: string;
      mentions: number;
    }>;
    pricePerceptions: Array<{
      perception: string;
      count: number;
    }>;
    regionPotential: Array<{
      potential: string;
      count: number;
    }>;
  };
}

interface StatisticsFilters {
  startDate: string;
  endDate: string;
  representative: string;
  status: string;
}

export function StatisticsTab() {
  const [filters, setFilters] = useState<StatisticsFilters>({
    startDate: '',
    endDate: '',
    representative: '',
    status: ''
  });
  
  const [activeTab, setActiveTab] = useState('overview');
  const [drillDownData, setDrillDownData] = useState<DrillDownData | null>(null);
  const [showDrillDown, setShowDrillDown] = useState(false);

  // Fetch overview statistics
  const { data: overviewData, isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['statistics-overview'],
    queryFn: () => statisticsApi.getOverview(),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });

  // Fetch detailed statistics based on active tab (currently unused)
  // const { data: detailedData, isLoading: detailedLoading } = useQuery({
  //   queryKey: ['statistics-detailed', activeTab, filters],
  //   queryFn: () => statisticsApi.getDetailedStats(activeTab, filters),
  //   enabled: activeTab !== 'overview',
  // });

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const data = await statisticsApi.exportData(format, filters);
      
      if (format === 'csv') {
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `statistics_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `statistics_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Chyba při exportu:', error);
    }
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      representative: '',
      status: ''
    });
  };

  const handleDrillDown = (data: DrillDownData) => {
    setDrillDownData(data);
    setShowDrillDown(true);
  };

  const closeDrillDown = () => {
    setShowDrillDown(false);
    setDrillDownData(null);
  };

  // Helper function to render simple bar chart
  const renderBarChart = (
    data: Array<{ label: string; value: number; color?: string; drillDown?: DrillDownData }>, 
    maxValue?: number
  ) => {
    const max = maxValue || Math.max(...data.map(d => d.value));
    
    return (
      <div className="space-y-3">
        {data.map((item, index) => (
          <div 
            key={index} 
            className={`flex items-center gap-3 ${item.drillDown ? 'cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors' : ''}`}
            onClick={() => item.drillDown && handleDrillDown(item.drillDown)}
            title={item.drillDown ? `Kliknutím zobrazíte detaily pro ${item.label}` : undefined}
          >
            <div className="w-24 text-sm text-muted-foreground truncate" title={item.label}>
              {item.label}
            </div>
            <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full ${item.color || 'bg-primary'} transition-all duration-300`}
                style={{ width: `${(item.value / max) * 100}%` }}
              />
            </div>
            <div className="w-12 text-sm font-medium text-right flex items-center gap-1">
              {item.value}
              {item.drillDown && <MousePointer2 className="h-3 w-3 text-muted-foreground" />}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Helper function to translate status
  const translateStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'draft': 'Koncept',
      'submitted': 'Odeslaný',
      'synced': 'Synchronizován',
      'error': 'Chyba'
    };
    return statusMap[status] || status;
  };

  // Helper function to translate design impressions
  const translateDesignImpression = (impression: string) => {
    const impressionMap: { [key: string]: string } = {
      'very-positive': 'Velmi pozitivní',
      'rather-positive': 'Spíše pozitivní',
      'neutral': 'Neutrální',
      'rather-negative': 'Spíše negativní',
      'very-negative': 'Velmi negativní'
    };
    return impressionMap[impression] || impression;
  };

  // Helper function to translate cooperation interest
  const translateCooperationInterest = (interest: string) => {
    const interestMap: { [key: string]: string } = {
      'very-interested': 'Velmi zajímá',
      'interested': 'Zajímá',
      'neutral': 'Neutrální',
      'not-interested': 'Nezajímá',
      'not-applicable': 'Nevztahuje se'
    };
    return interestMap[interest] || interest;
  };

  if (overviewLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (overviewError) {
    return (
      <div className="text-center p-8">
        <p className="text-red-500">Chyba při načítání statistik: {overviewError.message}</p>
      </div>
    );
  }

  const overview = overviewData as StatisticsOverview;

  return (
    <div className="space-y-6">
      {/* Header with filters and export */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Statistická analýza</h2>
          <p className="text-muted-foreground">Detailní přehled vyplněných formulářů a tržních poznatků</p>
        </div>
        
        <div className="flex gap-2">
          <PDFExport 
            data={overview} 
            title="BLAZE HARMONY - Statistická analýza" 
            reportType="overview" 
          />
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('json')}>
            <Download className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtry
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="startDate">Od data</Label>
              <Input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="endDate">Do data</Label>
              <Input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="representative">Obchodník</Label>
              <select
                id="representative"
                value={filters.representative}
                onChange={(e) => setFilters({ ...filters, representative: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Všichni</option>
                {overview?.representativePerformance?.map((rep) => (
                  <option key={rep.representative} value={rep.representative}>
                    {rep.representative}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="status">Stav</Label>
              <select
                id="status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Všechny</option>
                <option value="draft">Koncept</option>
                <option value="submitted">Odeslaný</option>
                <option value="synced">Synchronizován</option>
                <option value="error">Chyba</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={resetFilters}>
              Vymazat filtry
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Statistics Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Přehled</TabsTrigger>
          <TabsTrigger value="submissions">Formuláře</TabsTrigger>
          <TabsTrigger value="design-impressions">Design</TabsTrigger>
          <TabsTrigger value="cooperation-interest">Zájem</TabsTrigger>
          <TabsTrigger value="competitors">Konkurence</TabsTrigger>
          <TabsTrigger value="regional-analysis">Regiony</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Celkem formulářů</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview?.overview?.totalSubmissions || 0}</div>
                <p className="text-xs text-muted-foreground">
                  všechny stavy
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Míra konverze</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {overview?.overview?.conversionRate?.total > 0 
                    ? ((overview.overview.conversionRate.completed / overview.overview.conversionRate.total) * 100).toFixed(1)
                    : 0}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {overview?.overview?.conversionRate?.completed || 0} z {overview?.overview?.conversionRate?.total || 0} dokončeno
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Průměrná doba</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {overview?.overview?.avgResponseTime ? overview.overview.avgResponseTime.toFixed(1) : 0} dní
                </div>
                <p className="text-xs text-muted-foreground">
                  od vytvoření k odeslání
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Aktivní obchodníci</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview?.representativePerformance?.length || 0}</div>
                <p className="text-xs text-muted-foreground">
                  s vyplněnými formuláři
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Status Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                Rozložení stavů formulářů
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overview?.statusDistribution?.length > 0 ? (
                renderBarChart(
                  overview.statusDistribution.map(item => ({
                    label: translateStatus(item.status),
                    value: item.count,
                    color: item.status === 'synced' ? 'bg-green-500' : 
                           item.status === 'submitted' ? 'bg-orange-500' :
                           item.status === 'error' ? 'bg-red-500' : 'bg-gray-500',
                    drillDown: {
                      type: 'status',
                      title: `Formuláře se stavem: ${translateStatus(item.status)}`,
                      description: `Zobrazení všech ${item.count} formulářů se stavem ${translateStatus(item.status).toLowerCase()}`,
                      value: item.status,
                      filters: filters
                    }
                  }))
                )
              ) : (
                <p className="text-muted-foreground">Žádná data k zobrazení</p>
              )}
            </CardContent>
          </Card>

          {/* Representative Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Výkon obchodníků
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overview?.representativePerformance?.length > 0 ? (
                renderBarChart(
                  overview.representativePerformance.map(item => ({
                    label: item.representative,
                    value: item.count,
                    drillDown: {
                      type: 'representative',
                      title: `Formuláře obchodníka: ${item.representative}`,
                      description: `Zobrazení všech ${item.count} formulářů od obchodníka ${item.representative}`,
                      value: item.representative,
                      filters: filters
                    }
                  }))
                )
              ) : (
                <p className="text-muted-foreground">Žádná data k zobrazení</p>
              )}
            </CardContent>
          </Card>

          {/* Monthly Trends */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Měsíční trendy
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overview?.monthlyTrends?.length > 0 ? (
                renderBarChart(
                  overview.monthlyTrends.map(item => ({
                    label: new Date(item.month).toLocaleDateString('cs-CZ', { month: 'short', year: 'numeric' }),
                    value: item.count,
                    drillDown: {
                      type: 'monthly',
                      title: `Denní breakdown pro ${new Date(item.month).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })}`,
                      description: `Zobrazení denního rozložení ${item.count} formulářů v tomto měsíci`,
                      value: item.month,
                      filters: filters
                    }
                  }))
                )
              ) : (
                <p className="text-muted-foreground">Žádná data k zobrazení</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="submissions" className="space-y-6">
          <SubmissionsTable />
        </TabsContent>

        <TabsContent value="design-impressions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Dojmy z designu
              </CardTitle>
              <CardDescription>
                Jak zákazníci vnímají design kamen BLAZE HARMONY
              </CardDescription>
            </CardHeader>
            <CardContent>
              {overview?.marketInsights?.designImpressions?.length > 0 ? (
                renderBarChart(
                  overview.marketInsights.designImpressions.map(item => ({
                    label: translateDesignImpression(item.impression),
                    value: item.count,
                    color: item.impression?.includes('positive') ? 'bg-green-500' :
                           item.impression?.includes('negative') ? 'bg-red-500' : 'bg-gray-500',
                    drillDown: {
                      type: 'design',
                      title: `Design hodnocení: ${translateDesignImpression(item.impression)}`,
                      description: `Zobrazení všech ${item.count} formulářů s tímto hodnocením designu`,
                      value: item.impression,
                      filters: filters
                    }
                  }))
                )
              ) : (
                <p className="text-muted-foreground">Žádná data k zobrazení</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cooperation-interest" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Zájem o spolupráci
              </CardTitle>
              <CardDescription>
                Míra zájmu dealerů o spolupráci s BLAZE HARMONY
              </CardDescription>
            </CardHeader>
            <CardContent>
              {overview?.marketInsights?.cooperationInterest?.length > 0 ? (
                renderBarChart(
                  overview.marketInsights.cooperationInterest.map(item => ({
                    label: translateCooperationInterest(item.interest),
                    value: item.count,
                    color: item.interest?.includes('interested') ? 'bg-green-500' :
                           item.interest?.includes('not') ? 'bg-red-500' : 'bg-gray-500',
                    drillDown: {
                      type: 'cooperation',
                      title: `Zájem o spolupráci: ${translateCooperationInterest(item.interest)}`,
                      description: `Zobrazení všech ${item.count} formulářů s tímto zájmem o spolupráci`,
                      value: item.interest,
                      filters: filters
                    }
                  }))
                )
              ) : (
                <p className="text-muted-foreground">Žádná data k zobrazení</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="competitors" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Nejčastěji zmiňovaná konkurence
              </CardTitle>
              <CardDescription>
                Top 10 konkurentů podle počtu zmínek
              </CardDescription>
            </CardHeader>
            <CardContent>
              {overview?.marketInsights?.topCompetitors?.length > 0 ? (
                renderBarChart(
                  overview.marketInsights.topCompetitors.map(item => ({
                    label: item.competitor,
                    value: item.mentions,
                    drillDown: {
                      type: 'competition',
                      title: `Konkurent: ${item.competitor}`,
                      description: `Zobrazení všech formulářů, kde byl zmíněn konkurent ${item.competitor} (${item.mentions} zmínek)`,
                      value: item.competitor,
                      filters: filters
                    }
                  }))
                )
              ) : (
                <p className="text-muted-foreground">Žádná data k zobrazení</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="regional-analysis" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Regionální potenciál
              </CardTitle>
              <CardDescription>
                Jak dealeři hodnotí potenciál svého regionu
              </CardDescription>
            </CardHeader>
            <CardContent>
              {overview?.marketInsights?.regionPotential?.length > 0 ? (
                renderBarChart(
                  overview.marketInsights.regionPotential.map(item => ({
                    label: item.potential,
                    value: item.count,
                    color: item.potential?.includes('high') || item.potential?.includes('vysoký') ? 'bg-green-500' :
                           item.potential?.includes('low') || item.potential?.includes('nízký') ? 'bg-red-500' : 'bg-gray-500',
                    drillDown: {
                      type: 'regional',
                      title: `Regionální potenciál: ${item.potential}`,
                      description: `Zobrazení všech ${item.count} formulářů s tímto hodnocením regionálního potenciálu`,
                      value: item.potential,
                      filters: filters
                    }
                  }))
                )
              ) : (
                <p className="text-muted-foreground">Žádná data k zobrazení</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Drill-down Modal */}
      <StatisticsDrillDownModal 
        isOpen={showDrillDown}
        onClose={closeDrillDown}
        data={drillDownData}
      />
    </div>
  );
}